import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import GuestRoute from '../router/GuestRoute';
import FinancialRoute from '../router/FinancialRoute';
import AuthLayout from '../layouts/AuthLayout';
import MainLayout from '../layouts/MainLayout';
import LoginPage from '../pages/auth/LoginPage';
import RegisterPage from '../pages/auth/RegisterPage';
import ForgotPasswordPage from '../pages/auth/ForgotPasswordPage';
import CustomerServicePage from '../pages/auth/CustomerServicePage';
import LanguagePage from '../pages/settings/LanguagePage';
import SettingsPage from '../pages/settings/SettingsPage';
import GoogleLogin from '../components/auth/GoogleLogin';
import HomePage from '../pages/home/HomePage';
import ProductsPage from '../pages/products/ProductsPage';
import MarketPage from '../pages/market/MarketPage';
import MessagesPage from '../pages/messages/MessagesPage';
import WalletPage from '../pages/WalletPage';
import FundDetailsPage from '../pages/funds/FundDetailsPage';
import DepositMerchantListPage from '../pages/deposit/DepositMerchantListPage';
import OnlineDepositPage from '../pages/deposit/OnlineDepositPage';
import OfflineCurrencyListPage from '../pages/deposit/OfflineCurrencyListPage';
import OfflineDepositPage from '../pages/deposit/OfflineDepositPage';
import WithdrawPage from '../pages/withdraw/WithdrawPage';
import WithdrawMethodsPage from '../pages/withdraw/WithdrawMethodsPage';
import WithdrawAddressesPage from '../pages/withdraw/WithdrawAddressesPage';
import WithdrawBindPage from '../pages/withdraw/WithdrawBindPage';
import WithdrawUnbindPage from '../pages/withdraw/WithdrawUnbindPage';
import SecurityCenterPage from '../pages/security/SecurityCenterPage';
import WithdrawPasswordPage from '../pages/security/WithdrawPasswordPage';
import LoginPasswordPage from '../pages/security/LoginPasswordPage';
import KycHubPage from '../pages/kyc/KycHubPage';
import KycFormPage from '../pages/kyc/KycFormPage';
import KycInfoPage from '../pages/kyc/KycInfoPage';
import OrdersPage from '../pages/orders/OrdersPage';
import TimedTradePage from '../pages/trade/TimedTradePage';
import FuturesDeliveryTradePage from '../pages/trade/FuturesDeliveryTradePage';
import FollowOrderSignalsPage from '../pages/trade/FollowOrderSignalsPage';
import FollowOrderListPage from '../pages/trade/FollowOrderListPage';
import FollowOrderConfirmPage from '../pages/trade/FollowOrderConfirmPage';
import PerpetualTradePage from '../pages/trade/PerpetualTradePage';
import NewsListPage from '../pages/news/NewsListPage';
import NewsDetailPage from '../pages/news/NewsDetailPage';
import AnnouncementDetailPage from '../pages/messages/AnnouncementDetailPage';
import InboxDetailPage from '../pages/messages/InboxDetailPage';
import HelpCenterListPage from '../pages/help/HelpCenterListPage';
import HelpCenterDetailPage from '../pages/help/HelpCenterDetailPage';
import AgreementPage from '../pages/legal/AgreementPage';
import PrivacyPolicyPage from '../pages/legal/PrivacyPolicyPage';
import InvitePage from '../pages/invite/InvitePage';
import BackfillInviterCodePage from '../pages/invite/BackfillInviterCodePage';
import ChatPage from '../pages/chat/ChatPage';
import SupportChatPage from '../pages/chat/SupportChatPage';
import ContactsPage from '../pages/chat/ContactsPage';
import FavoritesPage from '../pages/chat/FavoritesPage';
import FriendRequestsPage from '../pages/chat/FriendRequestsPage';
import UserSearchPage from '../pages/chat/UserSearchPage';
import BlockedUsersPage from '../pages/chat/BlockedUsersPage';
import GroupsPage from '../pages/chat/GroupsPage';
import CreateGroupPage from '../pages/chat/CreateGroupPage';
import AddGroupMembersPage from '../pages/chat/AddGroupMembersPage';
import ReportUserPage from '../pages/chat/ReportUserPage';
import ProtectedRoute from '../router/ProtectedRoute';
import ChatEnabledRoute from '../router/ChatEnabledRoute';
import ServiceRoute from '../router/ServiceRoute';

/** React Router v7 升級預先啟用，避免開發環境 deprecation 警告 */
const ROUTER_FUTURE = {
  v7_startTransition: true,
  v7_relativeSplatPath: true,
};

export default function AppRouter() {
  return (
    <BrowserRouter future={ROUTER_FUTURE}>
      <Routes>
        {/* 公開主殼：首頁為預設進入點，底部導覽固定 */}
        <Route element={<MainLayout />}>
          <Route path="/" element={<HomePage />} />
          {/* 服務開關（REACT_APP_DISABLED_SERVICES）：停用時顯示「功能尚未開放」，不載入頁面、不呼叫 API */}
          <Route element={<ServiceRoute service="order" />}>
            <Route path="/products" element={<ProductsPage />} />
          </Route>
          <Route element={<ServiceRoute service="market" />}>
            <Route path="/market" element={<MarketPage />} />
          </Route>
          <Route path="/messages" element={<MessagesPage />} />
          <Route path="/messages/inbox/:id" element={<InboxDetailPage />} />
          <Route path="/messages/announcements/:id" element={<AnnouncementDetailPage />} />
          <Route path="/help" element={<HelpCenterListPage />} />
          <Route path="/help/:id" element={<HelpCenterDetailPage />} />
          <Route path="/agreement" element={<AgreementPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/news" element={<NewsListPage />} />
          <Route path="/news/:id" element={<NewsDetailPage />} />
          <Route path="/support" element={<CustomerServicePage />} />
          <Route path="/language" element={<Navigate to="/settings/language" replace />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/settings/language" element={<LanguagePage />} />
          <Route element={<ServiceRoute service="order" />}>
            <Route path="/trade/timed" element={<TimedTradePage />} />
            <Route path="/trade/futures" element={<FuturesDeliveryTradePage />} />
            <Route path="/trade/perpetual" element={<PerpetualTradePage />} />
          </Route>

          {/* IM 聊天室（不得覆蓋 /messages）；社交子路由須在 :conversationId 之前 */}
          <Route element={<ServiceRoute service="im" />}>
            <Route element={<ProtectedRoute />}>
              <Route path="/chat/support" element={<SupportChatPage />} />
              <Route element={<ChatEnabledRoute />}>
                <Route path="/chat/contacts" element={<ContactsPage />} />
                <Route path="/chat/favorites" element={<FavoritesPage />} />
                <Route path="/chat/friend-requests" element={<FriendRequestsPage />} />
                <Route path="/chat/search" element={<UserSearchPage />} />
                <Route path="/chat/blocked" element={<BlockedUsersPage />} />
                <Route path="/chat/groups/new" element={<CreateGroupPage />} />
                <Route path="/chat/groups/:conversationId/add" element={<AddGroupMembersPage />} />
                <Route path="/chat/groups" element={<GroupsPage />} />
                <Route path="/chat/report" element={<ReportUserPage />} />
                <Route path="/chat" element={<ChatPage />} />
                <Route path="/chat/:conversationId" element={<ChatPage />} />
              </Route>
            </Route>
          </Route>

          {/* 金額／資金操作需登入 */}
          <Route element={<FinancialRoute />}>
            <Route element={<ServiceRoute service="payment" />}>
              <Route path="/wallet" element={<WalletPage />} />
              <Route path="/wallet/details" element={<FundDetailsPage />} />
              <Route path="/deposit" element={<DepositMerchantListPage />} />
              <Route path="/deposit/online/:merchantId" element={<OnlineDepositPage />} />
              <Route path="/deposit/offline" element={<OfflineCurrencyListPage />} />
              <Route path="/deposit/offline/:addressId" element={<OfflineDepositPage />} />
              <Route path="/withdraw" element={<WithdrawPage />} />
              <Route path="/withdraw/methods" element={<WithdrawMethodsPage />} />
              <Route path="/withdraw/addresses" element={<WithdrawAddressesPage />} />
              <Route path="/withdraw/addresses/bind" element={<WithdrawBindPage />} />
              <Route path="/withdraw/addresses/unbind" element={<WithdrawUnbindPage />} />
            </Route>
            <Route path="/security" element={<SecurityCenterPage />} />
            <Route path="/security/withdraw-password" element={<WithdrawPasswordPage />} />
            <Route path="/security/login-password" element={<LoginPasswordPage />} />
            <Route path="/kyc" element={<KycHubPage />} />
            <Route path="/kyc/form" element={<KycFormPage />} />
            <Route path="/kyc/info" element={<KycInfoPage />} />
            <Route element={<ServiceRoute service="payment" />}>
              <Route path="/transactions" element={<Navigate to="/wallet/details" replace />} />
            </Route>
            <Route element={<ServiceRoute service="order" />}>
              <Route path="/orders" element={<OrdersPage />} />
              <Route path="/trade/futures/orders" element={<Navigate to="/orders" replace />} />
              <Route path="/trade/futures/follow/signals" element={<FollowOrderSignalsPage />} />
              <Route path="/trade/futures/follow/list" element={<FollowOrderListPage />} />
              <Route path="/trade/futures/follow/:followOrderId" element={<FollowOrderConfirmPage />} />
            </Route>
            <Route path="/invite" element={<InvitePage />} />
            <Route path="/backfill-inviter" element={<BackfillInviterCodePage />} />
          </Route>
        </Route>

        {/* 登入／註冊（未登入才可進入） */}
        <Route element={<GuestRoute />}>
          <Route element={<AuthLayout />}>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/auth/google" element={<GoogleLogin />} />
          </Route>
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
